import { Controller } from "@hotwired/stimulus";
import Rails from "@rails/ujs";

// 親コンポーネント
// <div class="container" data-controller="search-filter">
//   フォーム
//   <form data-search-filter-target="form">
//   結果表示エリア (optional)
//   <div data-search-filter-target="results">
//   フォームリセット
//   <input type="reset" data-taget="search-filter.reset">
//   結果表示ボタン. 表示エリア(resultsTarget)がない場合は車両一覧へのリンク
//   <a data-dismiss="modal" data-search-filter-target="showResults resultButtonPresent">
//   空だった時の結果表示ボタン
//   <div data-search-filter-target="resultButtonBlank">
// </div>
export default class extends Controller {
  static targets = [
    "form",
    "results",
    "reset",
    "showResults",
    "resultButtonBlanks",
    "resultButtonPresent",
    "resultCountOutput",
    "carNameCounter",
    "gradeCounter",
    "selectedCarNames",
    "selectedGrades",
    "carNameCheckbox",
    "gradeCheckbox",
    "gradeSelectArea",
    "gradeHiddenInputsContainer",
    "excludeSoldoutCheckbox",
    "deliveryNegotiableCheckbox",
    "gradeModalTrigger",
  ];

  initialize() {
    this.serializer = new XMLSerializer();
  }

  connect() {
    if (!this.hasFormTarget) return;

    this.url = this.formTarget.action;
    this.initializeState();
    this.bind();
  }

  initializeState() {
    if (!this.hasResultsTarget) return;

    history.replaceState(
      this.serializer.serializeToString(this.resultsTarget),
      null,
      location.url,
    );
  }

  bind() {
    // グレード用
    this.#initializeHiddenInputsFromQuery();
    // カウント切り替え用
    this.displayFilteredResultCounts();

    // on submit
    this.formTarget.addEventListener("submit", (ev) => {
      ev.preventDefault();
      this.submit();
    });

    // form changes
    this.formTarget.querySelectorAll(".search-param").forEach((x) => {
      x.addEventListener("change", (event) => {
        const el = event.target;
        if (el.matches('[data-search-filter-target="carNameCheckbox"]')) {
          if (!this.#handleCarNameCheckLimit(event)) return;
          this.submit();
          return;
        }
        if (el.matches('[data-search-filter-target="gradeCheckbox"]')) {
          // grade は updateSelectedGrades 側で submit 済み
          return;
        }
        // その他のパラメータは通常送信
        this.submit();
      });
    });

    // input[type=reset]
    this.bindResetForm();

    // 結果表示エリアが無い場合は結果表示ボタンを通常のリンクにして終了
    if (!this.hasResultsTarget) {
      this.showResultsTargets.forEach((x) => {
        x.dataset.dismiss = null;
      });
      return;
    }
    // pagination links
    this.bindPaginationEvents();
    // on history-back
    window.onpopstate = (ev) => {
      if (!ev.state || !ev.state.result) return;

      ev.preventDefault();
      this.replaceWithState(ev.state);
    };
  }

  // state から状態を復元
  replaceWithState(state) {
    this.resultsTarget.innerHTML = state.result;
    // ページャーのイベントは剥がれるので再設定
    this.bindPaginationEvents();
    // 検索オプションはモーダル外で変更できるので再設定
    this.formTarget["sort_by"].value = state.values["sort_by"];
    this.formTarget["exclude_soldout"].checked =
      state.values["exclude_soldout"];

    const title = document.getElementById("search-title");
    if (title) {
      title.textContent = state.title;
    }
    const breadcrumb = document.querySelector(".breadcrumb");
    if (breadcrumb) {
      breadcrumb.innerHTML = state.breadcrumb;
    }
    const canonicalUrl = document.getElementById("canonical_link");
    if (canonicalUrl) {
      canonicalUrl.href = state.canonicalUrl;
    }
  }

  bindPaginationEvents() {
    this.resultsTarget.querySelectorAll("a.page-link").forEach((x) => {
      x.addEventListener("click", (ev) => {
        const url = ev.currentTarget.href;
        if (!url) return;
        ev.preventDefault();
        this.resultsLoading();

        Rails.ajax({
          type: "GET",
          url: url,
          beforeSend: (xhr) => this.beforeSend(xhr),
          success: (html, _, xhr) => this.replaceSearchResult(html, xhr),
          error: null,
          complete: () => {
            this.xhr = null;
          },
        });
      });
    });
  }

  // クエリ付きURLに直に来た場合はパラメーターが初期値になるので全部舐める
  bindResetForm() {
    this.resetTargets.forEach((x) => {
      x.addEventListener("click", (ev) => {
        ev.preventDefault();

        this.formTarget
          .querySelectorAll(
            "input[type=text].search-param, select.search-param",
          )
          .forEach((f) => (f.value = ""));
        this.formTarget
          .querySelectorAll("input[type=checkbox].search-param")
          .forEach((f) => (f.checked = false));

        // 車種、グレード表示の対応
        this.#resetCarAndGradeSelection();
        this.submit();
      });
    });
  }

  submit() {
    const data = new FormData(this.formTarget);
    const segment = new URL(location.href).searchParams.get("segment") || "";
    data.append("segment", segment);
    const params = new URLSearchParams(data).toString();
    const newUrl = `${window.location.pathname}?${params}`;
    history.replaceState(history.state ?? null, "", newUrl);

    this.formTarget.classList.add("searching");
    this.resultsLoading();

    // parameters: https://github.com/rails/rails/blob/v6.1.7.1/actionview/app/assets/javascripts/rails-ujs/utils/ajax.coffee#L19-L26
    Rails.ajax({
      type: "GET",
      url: this.url,
      data: params,
      beforeSend: (xhr) => this.beforeSend(xhr),
      success: (html, _, xhr) => this.replaceSearchResult(html, xhr),
      error: null,
      complete: () => {
        this.xhr = null;
        this.formTarget.classList.remove("searching");
      },
    });
  }

  /**
   * 前回の検索条件で検索する
   * @param {Event} event
   */
  prevFilter(event) {
    this.formTarget.classList.add("searching");
    this.resultsLoading();

    // parameters: https://github.com/rails/rails/blob/v6.1.7.1/actionview/app/assets/javascripts/rails-ujs/utils/ajax.coffee#L19-L26
    Rails.ajax({
      type: "GET",
      url: event.target.href,
      beforeSend: (xhr) => this.beforeSend(xhr),
      success: (html, _, xhr) => this.replaceSearchResult(html, xhr),
      error: null,
      complete: () => {
        this.xhr = null;
        this.formTarget.classList.remove("searching");
      },
    });
  }

  beforeSend(xhr) {
    if (this.xhr) {
      this.xhr.abort();
    }
    this.xhr = xhr;
    // false だと送信しない
    return true;
  }

  replaceSearchResult(html, xhr) {
    this.updateTotalCount(html);
    // 結果表示エリアがなければボタンにリンク先を設定して終了
    if (!this.hasResultsTarget) {
      this.setShowResultsURL(xhr.responseURL);
      return;
    }

    const node = html.getElementById(this.resultsTarget.id);
    this.resultsTarget.replaceWith(node);
    const state = {
      result: node.innerHTML,
      values: this.getSearchOptionValues(),
      title: null,
      breadcrumb: null,
      canonicalUrl: null,
    };

    // 検索タイトルを更新
    const titleNew = html.getElementById("search-title");
    if (titleNew) {
      state.title = titleNew.textContent;
      const title = document.getElementById("search-title");
      if (title) {
        title.textContent = titleNew.textContent;
      }
    }
    // パンクズを更新
    const breadcrumbNew = html.querySelector(".breadcrumb");
    if (breadcrumbNew) {
      state.breadcrumb = breadcrumbNew.innerHTML;
      const breadcrumb = document.querySelector(".breadcrumb");
      if (breadcrumb) {
        breadcrumb.replaceWith(breadcrumbNew);
      }
    }
    // canonical url を更新 (クローラーはフォームいじらないと思うけど指摘あったし一応)
    const canonicalUrlNew = html.getElementById("canonical_link");
    if (canonicalUrlNew) {
      state.canonicalUrl = canonicalUrlNew.href;
      const canonicalUrl = document.getElementById("canonical_link");
      if (canonicalUrl) {
        canonicalUrl.href = canonicalUrlNew.href;
      }
    }
    this.bindPaginationEvents();

    history.pushState(state, null, xhr.responseURL);
  }

  getSearchOptionValues() {
    return {
      sort_by: this.formTarget["sort_by"].value,
      exclude_soldout: this.formTarget["exclude_soldout"].checked,
    };
  }

  updateTotalCount(html) {
    const el = html.getElementById("exhibited-cars-total-count");
    const blanks = this.resultButtonBlanksTargets;
    const presents = this.resultButtonPresentTargets;

    if (!el) {
      // 検索結果なし
      blanks.forEach((b) => b.classList.remove("d-none"));
      presents.forEach((p) => p.classList.add("d-none"));
    } else {
      blanks.forEach((b) => b.classList.add("d-none"));
      presents.forEach((p) => p.classList.remove("d-none"));
      this.resultCountOutputTarget.textContent = el.textContent;
    }
  }

  // 検索結果表示ボタンにリンク先を設定, 表示エリアがない時用
  setShowResultsURL(url) {
    this.showResultsTargets.forEach((x) => {
      x.href = url;
    });
  }

  resultsLoading() {
    if (!this.hasResultsTarget) return;

    $("html").animate({ scrollTop: 0 }, 128);
    this.resultsTarget.style.opacity = 0.6;
  }

  #getCarNameCheckedCount() {
    const ids = new Set(
      this.carNameCheckboxTargets
        .filter((cb) => cb.checked)
        .map((cb) => cb.dataset.carId),
    );
    return ids.size;
  }

  #getCarGradeCheckedCount() {
    const parents = this.gradeCheckboxTargets.filter(
      (cb) => !cb.dataset.parentId, // parentId が無いのが親
    );

    let count = 0;

    parents.forEach((parent) => {
      if (parent.checked) {
        // 親がONなら子は無視して1件だけ
        count += 1;
      } else {
        // 親がOFFなら子のチェック数を数える
        const childCheckboxes = this.gradeCheckboxTargets.filter(
          (cb) => cb.dataset.parentId === parent.dataset.id,
        );
        count += childCheckboxes.filter((cb) => cb.checked).length;
      }
    });

    return count;
  }

  #handleCarNameCheckLimit(event) {
    const checkedCount = this.#getCarNameCheckedCount();

    if (checkedCount > 10) {
      alert("車種を選択できるのは10件までです。");

      // ここで submitが実行されないよう、強制的に無効化
      event.preventDefault();
      event.stopImmediatePropagation();
      event.target.checked = false;
      this.updateCarNameCount();
      return false;
    }

    return true;
  }

  #handleCarGradeCheckLimit(event) {
    const checkedCount = this.#getCarGradeCheckedCount();

    if (checkedCount > 10) {
      alert("グレードを選択できるのは10件までです。");
      event.preventDefault();
      event.stopImmediatePropagation();
      event.target.checked = false;
      this.updateCarGradeCount();
      return false;
    }

    return true;
  }

  // 車種の選択数を更新
  updateCarNameCount() {
    // チェックされている車種（重複あり）
    const checked = this.carNameCheckboxTargets.filter(
      (checkbox) => checkbox.checked,
    );
    const uniqueChecked = Array.from(
      new Set(checked.map((checkbox) => checkbox.dataset.carId)),
    );
    const checkedCount = uniqueChecked.length;
    this.carNameCounterTargets.forEach((counter) => {
      counter.textContent = checkedCount;
    });
  }

  // グレードの選択数を更新
  updateCarGradeCount() {
    const checkedCount = this.#getCarGradeCheckedCount();
    this.gradeCounterTargets.forEach((counter) => {
      counter.textContent = checkedCount;
    });
  }

  // ------------------- 車種 -------------------
  // 選択肢の車種件数を条件によって切り替える処理
  displayFilteredResultCounts() {
    const excludeSoldout =
      this.hasExcludeSoldoutCheckboxTarget &&
      this.excludeSoldoutCheckboxTarget.checked;
    const deliveryNegotiable =
      this.hasDeliveryNegotiableCheckboxTarget &&
      this.deliveryNegotiableCheckboxTarget.checked;

    let type;
    if (excludeSoldout && deliveryNegotiable) {
      type = "exclude_soldout_and_delivery_negotiable"; // 販売中のみかつ納期交渉可のみ
    } else if (excludeSoldout) {
      type = "exclude_soldout"; // 販売中のみ
    } else if (deliveryNegotiable) {
      type = "delivery_negotiable"; // 納期交渉可のみ
    } else {
      type = "total"; // チェックなし（全体）
    }

    this.#toggleCarCounts(type);
  }

  #toggleCarCounts(type) {
    // 件数バッジ表示切替
    document.querySelectorAll(".search-result-count").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.type !== type);
    });

    // 件数 0 のアイテムを非表示に
    document
      .querySelectorAll(".list-group-item.search-list-group-item")
      .forEach((li) => {
        const span = li.querySelector(
          `.search-result-count[data-type="${type}"]`,
        );
        const count = span ? Number(span.textContent.replace(/\D/g, "")) : 0;
        li.classList.toggle("d-none", count === 0);
      });
  }

  // 車種をチェックした時の処理
  updateCarGradeSelection(event) {
    // 10件制限：チェック時に制限超えたら止める
    if (
      event &&
      event.target.checked &&
      !this.#handleCarNameCheckLimit(event)
    ) {
      event.target.checked = false;
      return;
    }

    if (event) {
      const carId = event.target.dataset.carId;

      // carId が同じチェックボックスを全て同期
      this.carNameCheckboxTargets.forEach((cb) => {
        if (cb.dataset.carId === carId) {
          cb.checked = event.target.checked;
        }
      });
    }

    if (event && !event.target.checked) {
      // チェックを外した場合
      this.removeSelectedCarNames(event);
    } else {
      // チェックした場合 → ユニーク carId ごとにラベル作成
      const selectedItems = Array.from(
        new Map(
          this.carNameCheckboxTargets
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => [
              checkbox.dataset.carId,
              {
                label: checkbox.dataset.label,
                carId: checkbox.dataset.carId,
                value: checkbox.value,
              },
            ]),
        ).values(),
      );
      this.#displaySelectedCarNames(selectedItems);
    }

    // グレード一覧取得は常に行う
    requestAnimationFrame(() => {
      this.#fetchGradeOptions();
    });

    // 件数更新は最後にユニーク carId ベースで
    this.updateCarNameCount();
  }

  // 指定した車種を外した時の処理
  removeSelectedCarNames(event) {
    const { label: labelToRemove, carId: carIdToRemove } = event.target.dataset;

    // 車種チェックボックス（重複分含む）を全部外す
    this.carNameCheckboxTargets.forEach((cb) => {
      if (cb.dataset.carId === carIdToRemove) cb.checked = false;
    });

    // 内部データから削除
    this.#removeSelectedCar(labelToRemove, carIdToRemove);

    // ×ボタン押下の場合のみ検索を再実行
    if (event.type === "click") {
      this.submit();
      this.updateCarNameCount();
    }

    // グレード選択リストと表示を更新
    this.#fetchGradeOptions()
      .then(() => {
        if (this.hasSelectedCarNamesTarget) {
          // 表示上の「同じ carId の車種アイテム」をすべて削除
          this.selectedCarNamesTarget
            .querySelectorAll(
              `[data-car-id="${carIdToRemove}"].display-car-name`,
            )
            .forEach((el) => el.closest("li")?.remove());

          // 選択中の車種がなくなった場合は「指定なし」を表示
          if (!this.selectedCarNamesTarget.children.length) {
            this.#resetSelectionDisplay();
          }
        }
      })
      .catch((error) => {
        console.error("処理中にエラーが発生しました:", error);
      });
  }

  // 選択した車種に紐づくグレード一覧取得（グレード選択画面用）
  #fetchGradeOptions() {
    if (!this.hasGradeSelectAreaTarget) return;

    return fetch(
      `/exhibited_cars/search_grade_options?${new URLSearchParams(window.location.search).toString()}`,
      {
        method: "GET",
        // パーシャルだけを差し込むので、同意チェック等でフルページの HTML を
        // 返されないように XHR であることを伝える
        // （サーバー側は partial_response_actions でも判定するが、
        //   宣言のない URL を指定されたときの保険として付けておく）
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch grade form");
        }
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const gradeSelectElement = doc.getElementById("grade-select");
        const target = this.gradeSelectAreaTarget;

        if (gradeSelectElement && target) {
          target.innerHTML = gradeSelectElement.innerHTML;
        }
        this.#displaySelectedGrades();
        this.updateCarGradeCount();
      })
      .catch((error) => {
        console.error("Error fetching grade form:", error);
      });
  }

  // 選択した車種ラベルを作成
  #displaySelectedCarNames(selectedItems) {
    if (!this.hasSelectedCarNamesTarget) return;
    this.selectedCarNamesTarget.className = "";
    this.selectedCarNamesTarget.innerHTML = "";

    if (selectedItems.length === 0) {
      this.#resetSelectionDisplay();
    } else {
      // 重複を carId で排除
      const uniqueItems = Array.from(
        new Map(selectedItems.map((item) => [item.carId, item])).values(),
      );

      // 選択した車種表示のデザイン
      this.selectedCarNamesTarget.classList.add(
        "list-inline",
        "form-check-btn",
        "mr-n1",
        "mb-n1",
      );

      uniqueItems.forEach((item) => {
        const listItem = document.createElement("li");
        listItem.dataset.carId = item.carId;
        listItem.classList.add("list-inline-item", "display-car-name");

        const label = document.createElement("label");
        label.textContent = item.label;
        label.classList.add("btn-sm", "d-inline-flex", "align-items-center");

        // バツ印の削除ボタン作成
        const removeButton = document.createElement("i");
        removeButton.classList.add(
          "icon",
          "icon-sm",
          "delete-fill",
          "text-secondary",
          "my-n1",
          "ml-1",
        );
        removeButton.dataset.value = item.value;
        removeButton.dataset.label = item.label;
        removeButton.dataset.carId = item.carId;
        removeButton.dataset.action =
          "click->search-filter#removeSelectedCarNames";
        label.appendChild(removeButton);

        listItem.appendChild(label);
        this.selectedCarNamesTarget.appendChild(listItem);
      });
    }
  }

  // ------------------- グレード -------------------
  // グレードを選択した時の処理
  updateSelectedGrades(event) {
    const checkbox = event.target;
    const container = this.gradeHiddenInputsContainerTarget;
    const {
      carName,
      releaseDate,
      salesTerminatedDate = "",
      gradeId = "",
      parentId = "",
      id = "",
      gradeName = "",
    } = checkbox.dataset;

    const isParent = !parentId;
    const parentKey = `_${releaseDate}_${salesTerminatedDate}`;

    if (isParent) {
      const children = document.querySelectorAll(
        `input.search-param[data-parent-id="${id}"]`,
      );

      if (checkbox.checked) {
        // 親ON時に 10件制限を確認
        if (!this.#handleCarGradeCheckLimit(event)) {
          checkbox.checked = false;
          return;
        }

        // 親ON → 子を全部ON + 子のhiddenInput削除 + 親hiddenInput作成
        children.forEach((c) => (c.checked = true));
        this.#removeHiddenInputs(
          container,
          carName,
          releaseDate,
          salesTerminatedDate,
          "",
        );
        this.#addHiddenInput(
          container,
          `car_gs[${carName}][${parentKey}][g]`,
          id,
        );
      } else {
        // 親OFF → 子も全部OFF + hiddenInputs削除
        children.forEach((c) => (c.checked = false));
        this.#removeHiddenInputs(
          container,
          carName,
          releaseDate,
          salesTerminatedDate,
          "",
        );
      }
    } else {
      const parent = document.querySelector(
        `input.search-param[data-id="${parentId}"]`,
      );

      if (checkbox.checked) {
        // 子ON
        if (!this.#handleCarGradeCheckLimit(event)) {
          checkbox.checked = false;
          return;
        }

        const childKey = `${gradeName}_${releaseDate}_${salesTerminatedDate}`;
        this.#addHiddenInput(
          container,
          `car_gs[${carName}][${childKey}][g]`,
          gradeId,
        );

        // 親がONなら外す（子モードへ切替）
        if (parent?.checked) {
          parent.checked = false;
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            "",
          );
        }
      } else {
        // 子OFF
        if (parent?.checked) {
          // 親ON → 親を外して子全体モードに切替
          parent.checked = false;

          const siblings = document.querySelectorAll(
            `input.search-param[data-parent-id="${parentId}"]`,
          );
          siblings.forEach((s) => (s.checked = true));
          checkbox.checked = false;

          if (!this.#handleCarGradeCheckLimit(event)) {
            parent.checked = true;
            siblings.forEach((s) => (s.checked = true));
            return;
          }

          // hiddenInputsを子全体で再構築
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            "",
          );
          siblings.forEach((s) => {
            if (s.checked) {
              const key = `${s.dataset.gradeName}_${releaseDate}_${salesTerminatedDate}`;
              this.#addHiddenInput(
                container,
                `car_gs[${carName}][${key}][g]`,
                s.dataset.gradeId,
              );
            }
          });
        } else {
          // 親OFF時の通常子OFF
          this.#removeHiddenInputs(
            container,
            carName,
            releaseDate,
            salesTerminatedDate,
            gradeName,
          );
        }
      }
    }

    this.updateCarGradeCount();
    this.submit();
    this.#displaySelectedGrades();
  }

  // 選択したグレードの表示作成
  #displaySelectedGrades() {
    const selectedItemsMap = this.#parseSelectedGradesFromURL();
    requestAnimationFrame(() => {
      if (
        !this.selectedGradesTarget.textContent?.trim() ||
        !selectedItemsMap ||
        selectedItemsMap.size === 0
      ) {
        this.selectedGradesTarget.innerHTML = "指定なし";
      }
    });

    // 車種が選択されていない場合はグレード選択は非活性
    this.#toggleGradeSectionDisabled();

    // 選択されたデータをフォーマット
    const selectedItems = this.#formatSelectedItems(selectedItemsMap);

    // フォーマットされたデータをHTMLとして描画
    this.#renderSelectedGrades(selectedItems);
  }

  #toggleGradeSectionDisabled() {
    const disabled = !this.selectedCarNamesTarget.querySelector("li");

    this.gradeModalTriggerTarget.classList.toggle("disabled", disabled);
    this.selectedGradesTarget.classList.toggle("text-muted", disabled);
  }

  // URLのクエリパラメータから車種ごとのグレード選択情報をパースしてMapにまとめる
  #parseSelectedGradesFromURL() {
    const params = new URLSearchParams(window.location.search);
    const selectedItemsMap = new Map();

    params.forEach((value, key) => {
      const match = key.match(/car_gs\[(.*?)\]\[(.*?)\]/);
      if (!match) return;

      const [, carName, fieldOrGrade] = match;

      if (!selectedItemsMap.has(carName)) selectedItemsMap.set(carName, {});
      const gradeKey = fieldOrGrade === "c" ? null : fieldOrGrade;

      if (!gradeKey) return;

      const [gradeName, releaseDate, salesTerminatedDate] = gradeKey.split("_");

      selectedItemsMap.get(carName)[gradeKey] = {
        value: value,
        gradeName: gradeName,
        releaseDate: releaseDate,
        salesTerminatedDate: salesTerminatedDate,
      };
    });

    return selectedItemsMap;
  }

  #formatDate(dateString) {
    return dateString
      ? `${new Date(dateString).getFullYear()}年${(new Date(dateString).getMonth() + 1).toString().padStart(2, "0")}月`
      : "";
  }

  /**
   * 選択された項目を整形する
   * @param {Map} selectedItemsMap - 選択されたグレード情報のマップ
   * @returns {Array} 整形された選択項目のリスト
   */
  #formatSelectedItems(selectedItemsMap) {
    return [...selectedItemsMap.entries()].flatMap(([carName, entries]) =>
      Object.values(entries).map((details) => ({
        carName,
        gradeName: details.gradeName === "" ? "" : details.gradeName,
        releaseDate: this.#formatDate(details.releaseDate),
        salesTerminatedDate: this.#formatDate(details.salesTerminatedDate),
      })),
    );
  }

  #renderSelectedGrades(selectedItems) {
    const outputHtml = Object.entries(
      selectedItems.reduce(
        (acc, { carName, releaseDate, salesTerminatedDate, gradeName }) => {
          (acc[carName] ||= []).push(
            `${releaseDate}〜${salesTerminatedDate} ${gradeName}`.trim(),
          );
          return acc;
        },
        {},
      ),
    )
      .sort(([a], [b]) => a.localeCompare(b, "ja")) // carNameの五十音順にソート
      .map(([carName, grades]) => `${carName} ${grades.join(", ")}`)
      .join(" / ");

    this.selectedGradesTarget.innerHTML = outputHtml;
  }

  #removeSelectedCar(labelToRemove, carIdToRemove) {
    const container = this.gradeHiddenInputsContainerTarget;

    const relatedCheckboxes = this.gradeCheckboxTargets.filter((checkbox) => {
      return (
        checkbox.dataset.carName === labelToRemove ||
        checkbox.dataset.carId === carIdToRemove
      );
    });

    relatedCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
      this.#removeHiddenInputs(
        container,
        checkbox.dataset.carName,
        checkbox.dataset.releaseDate || "",
        checkbox.dataset.salesTerminatedDate || "",
        checkbox.dataset.gradeName || "",
      );
    });
  }

  #resetSelectionDisplay() {
    if (this.hasSelectedCarNamesTarget) {
      this.selectedCarNamesTarget.className = "ml-n4";
      this.selectedCarNamesTarget.innerHTML = "指定なし";
    }
    if (this.hasSelectedGradesTarget) {
      this.selectedGradesTarget.innerHTML = "指定なし";
    }
    this.#toggleGradeSectionDisabled();
  }

  // hidden input 追加処理
  #addHiddenInput(container, name, value) {
    if (
      value == null ||
      container.querySelector(`input[name="${name}"][value="${value}"]`)
    )
      return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    container.appendChild(input);
  }

  #removeHiddenInputs(
    container,
    carName,
    releaseDate,
    salesTerminatedDate,
    gradeName,
  ) {
    // 通常のキー削除
    if (releaseDate || salesTerminatedDate || gradeName) {
      const key = gradeName
        ? `${gradeName}_${releaseDate}_${salesTerminatedDate}`
        : `_${releaseDate}_${salesTerminatedDate}`;

      const prefix = `car_gs[${carName}][${key}]`;

      container
        .querySelectorAll(`input[name^="${prefix}"]`)
        .forEach((input) => input.remove());
    }

    // 常に [c] も削除しておく
    container
      .querySelectorAll(`input[name="car_gs[${carName}][c]"]`)
      .forEach((input) => input.remove());
  }

  // アクセスされた時の初期処理
  #initializeHiddenInputsFromQuery() {
    if (!this.hasGradeHiddenInputsContainerTarget) return;

    const container = this.gradeHiddenInputsContainerTarget;
    const params = new URLSearchParams(window.location.search);

    // クエリからの車種、グレードの選択
    params.forEach((value, key) => {
      const [, carName, gradeKey, attribute = ""] =
        key.match(/car_gs\[(.*?)\]\[(.*?)\](?:\[(.*?)\])?/) || [];

      if (!carName || !gradeKey) return;

      const inputName = `car_gs[${carName}][${gradeKey}]${attribute ? `[${attribute}]` : ""}`;
      this.#addHiddenInput(container, inputName, value);
    });
    this.updateCarGradeSelection();
  }

  // 選択をすべて外す
  #resetCarAndGradeSelection() {
    this.carNameCheckboxTargets.forEach((checkbox) => {
      const labelToRemove = checkbox.dataset.label;
      const carIdToRemove = checkbox.dataset.carId;
      this.#removeSelectedCar(labelToRemove, carIdToRemove);
    });

    // 選択肢の表示は全体件数に切り替える
    const type = "total";
    document.querySelectorAll(".search-result-count").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.type !== type);
    });
    this.updateCarGradeSelection();
  }
}
